import React, { createContext, useState, useEffect, useCallback } from 'react';
import { ListingLabelOverride, ListingLabel } from '@bb/automaton';
import { OnboardingPortalController } from 'api';
import { featureEnabled } from 'utils';
import AuthContext from './AuthContext';
import ExternalAuthContext from './ExternalAuthContext';
import { AuthContextUser } from './types';

type OverrideDetail = {
  label: string | null | undefined;
  defaultLabel: string;
  hidden: boolean;
};

export type OverrideMap = Record<
  keyof typeof DEFAULT_LABEL_MAP,
  OverrideDetail
>;

const DEFAULT_LABEL_MAP: Record<ListingLabel | 'ALL', string> = {
  MLS: 'MLS',
  EXCLUSIVE: 'Exclusive',
  ASSIGNMENT: 'Assignment',
  COMING_SOON: 'Coming Soon',
  NEW_CONSTRUCTION: 'New Construction',
  CONDO: 'Residential - Condominium',
  FREEHOLD: 'Residential - Freehold',
  LAND: 'Residential - Land',
  INCOME: 'Residential - Income',
  COMMERCIAL: 'Commercial',
  EXPIRED: 'Expired',
  PENDING: 'Pending',
  SOLD: 'Sold',
  TERMINATED: 'Terminated',
  SUSPENDED: 'Suspended',
  TEMP_OFF_MARKET: 'Temp Off Market',
  LEASED: 'Leased',
  UNKNOWN: 'Unknown',
  EXTENDED: 'Extended',
  NEW: 'New',
  COMBINED_LISTING: 'Combined Listing',
  PRICE_CHANGE: 'Price Change',
  SOLD_CONDITIONALLY: 'Sold Conditionally (Pending)',
  LEASED_CONDITIONALLY: 'Leased Conditionally (Pending)',
  DEAL_FELL_THROUGH: 'Deal Fell Through',
  WITHDRAWN: 'Withdrawn',
  ALL: 'All',
  BOARD: '',
  SHOW_FOR_BACKUPS: 'Show For Backups',
  STATUS: 'Status',
  AVAILABILITY: 'Availability',
  NEIGHBORHOOD: 'Neighborhood',
  NEIGHBORHOOD_DISPLAY: 'neighborhood',
  NEIGHBORHOOD_ANALYTICS: 'Neighborhood',
  REGION: 'Region',
  NEW_LISTINGS_TOOLTIP: '',
  SOLD_LEASED_TOOLTIP: '',
};

/** @deprecated Use Getter method instead. Will move to private once all usage are removed. */
export const parseLabelFromString = (
  value: ListingLabel | string,
): ListingLabel => {
  const normalized = value.trim().toUpperCase();
  // Assuming all follow this format
  return normalized.replace(/ /g, '_') as ListingLabel;
};

export const DEFAULT_VALUE = Object.entries(DEFAULT_LABEL_MAP).reduce(
  (acc, curr) => {
    const [label, value] = curr as [ListingLabel, string];
    acc[label] = { label: null, defaultLabel: value, hidden: false };
    return acc;
  },
  {} as OverrideMap,
);

export type ListingLabelContextType = {
  /** @deprecated Use Getter method instead. Will move to private once all usage are removed. */
  labelOverrideMap: OverrideMap;
  getLabel: (
    field: ListingLabel | string | undefined,
    defaultName?: string,
  ) => string | undefined;
  getDefaultLabel: (field: string) => string;
  isHidden: (field: string | undefined) => boolean;
  getAvailability: (
    availability: string | undefined,
    option?: { condensed: boolean },
  ) => string;
  getBoardName: (board: string, useOverride?: boolean) => string;
  getMemberNumberString: (board: string, number: string) => string;
};

const ListingLabelContext = createContext<ListingLabelContextType>({
  // in case context isn't available
  labelOverrideMap: DEFAULT_VALUE,
  getLabel: () => '',
  getDefaultLabel: () => '',
  isHidden: () => false,
  getAvailability: () => '',
  getBoardName: () => '',
  getMemberNumberString: () => '',
});

export const ListingLabelContextProvider: React.FC<{
  onboardingPortalId?: string;
  /**
   * this should be used when auth is available through $rootScope
   */
  authFromAngular?: AuthContextUser;
}> = ({ onboardingPortalId, authFromAngular, children }) => {
  const auth = React.useContext(AuthContext)?.auth || authFromAngular;
  const { auth: extAuth } = React.useContext(ExternalAuthContext);

  const [labelOverrideMap, setLabelOverrideMap] =
    useState<OverrideMap>(DEFAULT_VALUE);

  const getLabel = useCallback(
    (field: ListingLabel | string | undefined, defaultName?: string) => {
      if (!field) return defaultName;

      const parsedField = parseLabelFromString(field);

      // ENG-12220 For County & Neighborhood conversion
      const customDefault =
        parsedField === 'NEIGHBORHOOD' &&
        (parseLabelFromString(
          labelOverrideMap.NEIGHBORHOOD_DISPLAY?.label || '',
        ) as string) === 'COUNTY'
          ? 'County'
          : null;

      return (
        labelOverrideMap[parsedField]?.label ||
        defaultName ||
        customDefault ||
        (parsedField in labelOverrideMap
          ? labelOverrideMap[parsedField]?.defaultLabel
          : field)
      );
    },
    [labelOverrideMap],
  );

  const getDefaultLabel = useCallback(
    (field: string) =>
      labelOverrideMap[parseLabelFromString(field)]?.defaultLabel || field,
    [labelOverrideMap],
  );

  const isHidden = useCallback(
    (field: string | undefined) => {
      if (!field) return false;
      return labelOverrideMap[parseLabelFromString(field)]?.hidden || false;
    },
    [labelOverrideMap],
  );

  const getAvailability = useCallback(
    (
      availability: string | undefined,
      { condensed } = { condensed: false },
    ) => {
      if (!availability) return '';

      const parsedAvail =
        labelOverrideMap[parseLabelFromString(availability)]?.label ||
        labelOverrideMap[parseLabelFromString(availability)]?.defaultLabel ||
        availability;

      // overriden
      if (parsedAvail && parsedAvail !== availability) return parsedAvail;

      if (condensed) {
        return availability.includes('Conditionally')
          ? `${availability.split(' ')[0]} Cond. (Pending)`
          : availability;
      }

      return `${availability}${
        availability.includes('Conditionally') ? ' (Pending)' : ''
      }`;
    },
    [labelOverrideMap],
  );

  const getBoardName = useCallback(
    (board: string, useOverride = true) => {
      if (labelOverrideMap.BOARD?.label && useOverride) {
        return labelOverrideMap.BOARD.label;
      }

      if (board === 'RECO') return 'REco';
      return board;
    },
    [labelOverrideMap],
  );

  const getMemberNumberString = (
    board: string,
    number: string,
    useOverride = false,
  ) =>
    `${getBoardName(
      board.toUpperCase(),
      useOverride,
    )}: ${number.toUpperCase()}`;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const finalOnboardingPortalId =
          onboardingPortalId ||
          auth?.org.onboardingPortalId ||
          (extAuth?.user as any)?.org?.onboardingPortalId ||
          extAuth?.ownerOrg?.onboardingPortalId ||
          extAuth?.listing?.org.onboardingPortalId;

        let listingLabelData: Omit<
          ListingLabelOverride,
          'createdAt' | 'id' | 'onboardingPortalId' | 'updatedAt'
        >[];

        if (
          featureEnabled('labelOverridelInAuthFeature') &&
          finalOnboardingPortalId &&
          finalOnboardingPortalId === auth?.org?.onboardingPortal?.id
        ) {
          listingLabelData =
            auth.org.onboardingPortal?.listingLabelOverride ?? [];
        } else {
          listingLabelData =
            await OnboardingPortalController.getListingLabelOverride(
              finalOnboardingPortalId,
            );
        }

        const fetchedOverrideMap = listingLabelData.reduce<
          Partial<
            Record<
              keyof typeof DEFAULT_LABEL_MAP,
              Omit<OverrideDetail, 'defaultLabel'>
            >
          >
        >((acc, curr) => {
          acc[curr.label] = { label: curr.override, hidden: curr.hideLabel };
          return acc;
        }, {});

        const newMap = Object.keys(DEFAULT_VALUE).reduce(
          (acc, label: ListingLabel) => {
            acc[label] = {
              ...DEFAULT_VALUE[label],
              ...(fetchedOverrideMap?.[label] ?? {}),
            };
            return acc;
          },
          {} as OverrideMap,
        );

        setLabelOverrideMap(newMap);
      } catch (err) {
        console.error(err);
      }
    };

    fetchData();
  }, [
    auth?.org.onboardingPortalId,
    extAuth?.listing?.org.onboardingPortalId,
    extAuth?.ownerOrg?.onboardingPortalId,
    extAuth?.user,
  ]);

  return (
    <ListingLabelContext.Provider
      value={{
        labelOverrideMap,
        getLabel,
        getDefaultLabel,
        isHidden,
        getAvailability,
        getBoardName,
        getMemberNumberString,
      }}
    >
      {children}
    </ListingLabelContext.Provider>
  );
};

export default ListingLabelContext;
